import React from 'react';
import { CircleAlert, CircleCheck, CircleMinus, FileText, Repeat, Undo2, Users, Wallet } from 'lucide-react';
import type { InvoiceKind, StripeInvoiceRow } from '../../services/billingApi';

const KIND_ICON: Record<InvoiceKind, React.ElementType> = {
  plan: Repeat,
  seats: Users,
  credit: Undo2,
  topup: Wallet,
  other: FileText,
};

export const KIND_LABEL: Record<InvoiceKind, string> = {
  plan: 'Plan',
  seats: 'Seats',
  credit: 'Credit',
  topup: 'Top-up',
  other: 'Other',
};

export const invoiceDate = (created: number) =>
  new Date(created * 1000).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

/* A credit reads as money coming back, never as a negative charge. */
export const invoiceAmount = (cents: number) => {
  const value = `$${(Math.abs(cents) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (cents < 0) return `+${value}`;
  return value;
};

export const KindMark: React.FC<{ kind: InvoiceKind }> = ({ kind }) => {
  const Icon = KIND_ICON[kind] || FileText;
  return (
    <span aria-hidden className="grid h-9 w-9 shrink-0 place-items-center rounded-tile bg-canvas-inset text-ink-muted">
      <Icon size={16} strokeWidth={1.75} />
    </span>
  );
};

const STATUS: Record<string, { label: string; icon: React.ElementType; className: string }> = {
  paid: { label: 'Paid', icon: CircleCheck, className: 'text-[#3F7A2F]' },
  open: { label: 'Due', icon: CircleAlert, className: 'text-ink' },
  uncollectible: { label: 'Failed', icon: CircleAlert, className: 'text-danger' },
  void: { label: 'Cancelled', icon: CircleMinus, className: 'text-ink-soft' },
};

/* Status is a word with an icon, never colour alone. A credit is settled the
 * moment it is issued, so it reads as Credited rather than Paid. */
export const InvoiceStatus: React.FC<{ invoice: StripeInvoiceRow }> = ({ invoice }) => {
  let status = STATUS[invoice.status] || { label: invoice.status, icon: FileText, className: 'text-ink-muted' };
  if (invoice.kind === 'credit' && invoice.status === 'paid') status = { ...status, label: 'Credited' };
  const Icon = status.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap text-[12.5px] font-medium ${status.className}`}>
      <Icon size={14} strokeWidth={2} aria-hidden />
      {status.label}
    </span>
  );
};

export const AmountText: React.FC<{ cents: number; className?: string }> = ({ cents, className = '' }) => (
  <span className={`whitespace-nowrap tabular-nums ${cents < 0 ? 'text-[#3F7A2F]' : 'text-ink'} ${className}`}>
    {invoiceAmount(cents)}
  </span>
);
